import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

import {getUserInfo} from "../../../utils/auth";
import {useHashState} from 'utils/hashState';
import {ParcelInitialValue} from "../../../config/filterOptions";
import {ParcelColumns} from "./components/ParcelColumns";
import {OrderType} from "../../../graphql/types/OrderType";
import {OrderFiltersType} from "../../../graphql/types/OrderFiltersType";
import {useCentralParcelQuery} from "./hooks/useCentralParcelQuery";
import CentralParcelDetailDrawer from "./components/CentralParcelDetailDrawer";
import CentralParcelFilter from "./components/CentralParcelFilter";
import {useWarehouseContext} from "../../../contexts/WarehouseContext";
import { getLastWeekDateTimeRange } from '../../../utils/dateTimeUtils';

const CentralParcelsPage: React.FC = () => {
    const {t} = useTranslation();
    const [detailVisible, setDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');
    const { currentWarehouse } = useWarehouseContext();
    const [tempFilters, setTempFilters] = useHashState(ParcelInitialValue);
    const [filters, setFilters] = useHashState(ParcelInitialValue);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<OrderType[]>([]);

    // 设置 pickupHubId 持续跟随 currentWarehouse（左侧仓库切换）同步，而不只是首次填充默认值
    // （与下方 pickupWarehouseId 的同步逻辑保持一致，用值比较避免无意义的重复 setState）
    useEffect(() => {
        if (!currentWarehouse?.id) {
            return;
        }
        const currentHubId = String(currentWarehouse.id);
        setFilters((prev: OrderFiltersType) => {
            if (prev.pickupHubId?.length === 1 && prev.pickupHubId[0] === currentHubId) {
                return prev;
            }
            return { ...prev, pickupHubId: [currentHubId] };
        });
        setTempFilters((prev: OrderFiltersType) => {
            if (prev.pickupHubId?.length === 1 && prev.pickupHubId[0] === currentHubId) {
                return prev;
            }
            return { ...prev, pickupHubId: [currentHubId] };
        });
    }, [currentWarehouse?.id]);

    // 默认时间范围不写入 URL：只在页面加载时计算一次，避免每次 render 都生成新时间导致无限 refetch
    const defaultOrderTime = useMemo(() => getLastWeekDateTimeRange(), []);

    const getEffectiveOrderTime = (orderTime: any) => {
        // null：用户手动清空（点击叉号），保持为空，不回填默认值
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2 &&
            orderTime[0] !== null &&
            orderTime[1] !== null;
        return isValid ? orderTime : defaultOrderTime;
    };
    
    // 检查 trackingNumbers 是否有值
    const hasTrackingNumbers = useMemo(() => {
        return filters.trackingNumbers && filters.trackingNumbers.trim().length > 0;
    }, [filters.trackingNumbers]);
    
    // 使用自定义hook获取数据
    const {
        parcelData,
        loading,
        refetch,
        totalElements,
        accessiblePages,
        buildQueryVariables
    } = useCentralParcelQuery(
        {
            ...filters,
            orderTime: getEffectiveOrderTime(filters.orderTime)
        },
        currentPage,
        pageSize
    );

    const handleClearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: OrderType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const handleResetFilters = () => {
        setTempFilters(ParcelInitialValue);
        setFilters(ParcelInitialValue);
    };

    const handleFilter = () => {
        // 直接使用 tempFilters 的值来更新 filters，确保 hashState 同步
        // 需要明确设置所有字段，包括要删除的字段（设为 undefined）
        // 这样 updateHashState 才能包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
        const newFilters: any = {...tempFilters};
        // 确保 filters 中所有可能的字段都被包含，即使值是 undefined 或 null
        // 如果 tempFilters 中某个字段不存在，但 filters 中有，需要明确设置为 undefined
        // 这样可以确保旧字段被包含在更新中，encodeHash 会过滤它们
        const allKeys = new Set([...Object.keys(tempFilters), ...Object.keys(filters)]);
        allKeys.forEach(key => {
            if (!(key in newFilters)) {
                newFilters[key] = undefined;
            }
        });

        // 根据 trackingNumbers 是否有值来设置 pickupWarehouseId
        const hasTrackingNumbersValue = newFilters.trackingNumbers && newFilters.trackingNumbers.trim().length > 0;
        if (!hasTrackingNumbersValue && currentWarehouse?.id) {
            // trackingNumbers 没有值时，设置 pickupWarehouseId 为 currentWarehouse.id
            newFilters.pickupWarehouseId = Number(currentWarehouse.id);
        } else if (hasTrackingNumbersValue) {
            // trackingNumbers 有值时，清空 pickupWarehouseId
            newFilters.pickupWarehouseId = undefined;
        }

        // updateHashState 会保留 undefined 字段，encodeHash 会过滤它们，URL hash 中不会包含已删除的字段
        setFilters(newFilters);
        setCurrentPage(1);
    };

    // 根据 trackingNumbers 是否存在值来设置或清空 pickupWarehouseId
    useEffect(() => {
        if (!currentWarehouse?.id) {
            return;
        }
        
        setFilters((prevFilters: OrderFiltersType) => {
            const currentWarehouseId = Number(currentWarehouse.id);
            
            // trackingNumbers 存在值时，清空 pickupWarehouseId
            if (hasTrackingNumbers && prevFilters.pickupWarehouseId !== undefined && prevFilters.pickupWarehouseId !== null) {
                return {
                    ...prevFilters,
                    pickupWarehouseId: undefined
                };
            }
            // trackingNumbers 为空时，设置 pickupWarehouseId 为默认值
            else if (!hasTrackingNumbers && prevFilters.pickupWarehouseId !== currentWarehouseId) {
                return {
                    ...prevFilters,
                    pickupWarehouseId: currentWarehouseId
                };
            }
            return prevFilters;
        });
    }, [hasTrackingNumbers, currentWarehouse?.id, setFilters]);

    // 当筛选条件变化时清空选中状态
    useEffect(() => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    }, [filters]);

    const showParcelDetail = (record: OrderType) => {
        setOrderId(record.id);
        setDetailVisible(true);
    };
    
    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 筛选条件 */}
                <CentralParcelFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                    totalElements={totalElements}
                    selectedRows={selectedRows}
                    onClearSelection={handleClearSelection}
                />

                {/* 订单列表 */}
                <ParcelColumns
                    parcelData={parcelData}
                    showParcelDetail={showParcelDetail}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={loading}
                />

                {/* 分页：total 显示真实总数，maxPages 限制最大可翻页数（深度翻页保护）*/}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    maxPages={accessiblePages}
                />

                {/* 包裹详情抽屉组件 */}
                <CentralParcelDetailDrawer
                    visible={detailVisible}
                    onClose={() => setDetailVisible(false)}
                    orderId={orderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                    onRefreshList={() => refetch(buildQueryVariables())}
                />
            </div>
    );
};

export default CentralParcelsPage;
